// src/pages/Home.tsx
import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { FaLinkedin, FaGithub, FaArrowRight, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { useLang } from '../contexts/LanguageContext';

/* ── Animations ─────────────────────────────────── */
const gradientShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const ringFlow = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-12px); }
`;

/* ── Layout ─────────────────────────────────────── */
const HomeContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 6rem 2rem 4rem;
  position: relative;
  overflow: hidden;
  background: ${(props) => props.theme.gradients.background};
`;

/* mesh gradient orbs */
const Orb = styled.div<{ $color: string; $top: string; $left: string; $size: string }>`
  position: absolute;
  top: ${(p) => p.$top};
  left: ${(p) => p.$left};
  width: ${(p) => p.$size};
  height: ${(p) => p.$size};
  background: ${(p) => p.$color};
  border-radius: 50%;
  filter: blur(140px);
  opacity: 0.22;
  pointer-events: none;
`;

const Inner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 5rem;
  max-width: 1120px;
  width: 100%;
  z-index: 1;

  @media (max-width: 850px) {
    flex-direction: column-reverse;
    text-align: center;
    gap: 3rem;
  }
`;

const TextSide = styled(motion.div)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: 850px) {
    align-items: center;
  }
`;

/* ── Photo ──────────────────────────────────────── */
const PhotoSide = styled(motion.div)`
  flex-shrink: 0;
  animation: ${float} 6s ease-in-out infinite;

  @media (max-width: 850px) {
    animation: none;
  }
`;

const PhotoRing = styled.div`
  width: 296px;
  height: 296px;
  border-radius: 50%;
  padding: 3px;
  background: linear-gradient(135deg, #7c3aed, #a78bfa, #22c55e, #7c3aed);
  background-size: 300% 300%;
  animation: ${ringFlow} 5s ease infinite;

  @media (max-width: 850px) {
    width: 216px;
    height: 216px;
  }
`;

const PhotoInner = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  border: 4px solid ${(props) => props.theme.colors.primary};
  background: ${(props) => props.theme.colors.secondary};
`;

const Photo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
  display: block;
`;

/* ── Text elements ──────────────────────────────── */
const AvailableBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.45rem 1.1rem;
  background: rgba(34, 197, 94, 0.08);
  border: 1px solid rgba(34, 197, 94, 0.28);
  border-radius: 50px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #22c55e;
  margin-bottom: 1.4rem;
  letter-spacing: 0.02em;
`;

const PulseDot = styled.span`
  width: 7px;
  height: 7px;
  background: #22c55e;
  border-radius: 50%;
  display: inline-block;
  animation: homePulse 2.2s ease-in-out infinite;

  @keyframes homePulse {
    0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.5); }
    50%       { opacity: 0.8; box-shadow: 0 0 0 5px rgba(34, 197, 94, 0); }
  }
`;

const Greeting = styled(motion.p)`
  font-size: 0.95rem;
  font-weight: 500;
  color: ${(props) => props.theme.colors.muted};
  margin-bottom: 0.5rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const Title = styled(motion.h1)`
  font-size: clamp(2.8rem, 6vw, 5.2rem);
  font-weight: 800;
  letter-spacing: -0.04em;
  margin-bottom: 0.8rem;
  line-height: 1.0;
  color: ${(props) => props.theme.colors.white};

  span {
    background: linear-gradient(135deg, #7c3aed, #a78bfa, #c4b5fd, #7c3aed);
    background-size: 300% 300%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${gradientShift} 5s ease infinite;
  }
`;

const TypingContainer = styled(motion.p)`
  font-size: clamp(1rem, 2.2vw, 1.35rem);
  color: ${(props) => props.theme.colors.light};
  margin-bottom: 0.8rem;
  min-height: 1.8rem;
  font-weight: 400;
`;

const Cursor = styled.span`
  display: inline-block;
  width: 2px;
  height: 1.1em;
  background: ${(props) => props.theme.colors.accent};
  margin-left: 2px;
  vertical-align: text-bottom;
  animation: blink 1s step-end infinite;

  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
`;

const LocationLine = styled(motion.p)`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  color: ${(props) => props.theme.colors.muted};
  margin-bottom: 2.2rem;

  @media (max-width: 850px) {
    justify-content: center;
  }
`;

const ButtonGroup = styled(motion.div)`
  display: flex;
  gap: 1rem;
  margin-bottom: 2.2rem;
  flex-wrap: wrap;

  @media (max-width: 850px) {
    justify-content: center;
  }
`;

const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1.9rem;
  background: ${(props) => props.theme.gradients.accent};
  color: #fff;
  font-weight: 700;
  font-size: 0.9rem;
  border-radius: 50px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 24px rgba(124, 58, 237, 0.35);
  text-decoration: none;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 40px rgba(124, 58, 237, 0.5);
  }
`;

const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1.9rem;
  background: transparent;
  color: ${(props) => props.theme.colors.white};
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: 50px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  transition: all 0.3s ease;
  text-decoration: none;

  &:hover {
    border-color: rgba(139, 92, 246, 0.5);
    background: rgba(139, 92, 246, 0.07);
    transform: translateY(-2px);
  }
`;

const SocialRow = styled(motion.div)`
  display: flex;
  gap: 0.8rem;
  margin-bottom: 2.5rem;

  @media (max-width: 850px) {
    justify-content: center;
  }

  a {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: ${(props) => props.theme.colors.muted};
    font-size: 1rem;
    transition: all 0.3s ease;
    text-decoration: none;

    &:hover {
      color: ${(props) => props.theme.colors.accentLight};
      border-color: rgba(139, 92, 246, 0.5);
      background: rgba(139, 92, 246, 0.08);
      transform: translateY(-3px);
    }
  }
`;

const StatsRow = styled(motion.div)`
  display: flex;
  gap: 2.5rem;
  padding-top: 1.8rem;
  border-top: 1px solid rgba(255, 255, 255, 0.07);

  @media (max-width: 850px) {
    justify-content: center;
    gap: 2rem;
  }
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const StatValue = styled.span`
  font-size: 1.55rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  background: linear-gradient(135deg, #7c3aed, #a78bfa);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.1;
`;

const StatLabel = styled.span`
  font-size: 0.72rem;
  color: ${(props) => props.theme.colors.muted};
  font-weight: 500;
`;

/* ── Typing effect ──────────────────────────────── */
const roles = [
  'Full-Stack Developer',
  'React & TypeScript Expert',
  'Blockchain Engineer',
  'No-Code Master',
];

const useTypingEffect = () => {
  const [text, setText] = useState('');
  const [roleIndex, setRoleIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const current = roles[roleIndex];
    const speed = isDeleting ? 28 : 55;

    if (!isDeleting && charIndex === current.length) {
      const t = setTimeout(() => setIsDeleting(true), 1800);
      return () => clearTimeout(t);
    }
    if (isDeleting && charIndex === 0) {
      setIsDeleting(false);
      setRoleIndex((prev) => (prev + 1) % roles.length);
      return;
    }

    const t = setTimeout(() => {
      setCharIndex((prev) => prev + (isDeleting ? -1 : 1));
      setText(current.substring(0, charIndex + (isDeleting ? -1 : 1)));
    }, speed);
    return () => clearTimeout(t);
  }, [charIndex, isDeleting, roleIndex]);

  return text;
};

/* ── Component ──────────────────────────────────── */
const Home: React.FC = () => {
  const typedText = useTypingEffect();
  const { t } = useLang();

  return (
    <HomeContainer id="home">
      <Orb $color="rgba(124,58,237,0.6)"  $top="15%"  $left="5%"  $size="380px" />
      <Orb $color="rgba(34,197,94,0.4)"   $top="60%"  $left="75%" $size="280px" />
      <Orb $color="rgba(124,58,237,0.35)" $top="80%"  $left="20%" $size="240px" />

      <Inner>
        <TextSide
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
        >
          <AvailableBadge
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <PulseDot /> {t.home.available}
          </AvailableBadge>

          <Greeting
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {t.home.greeting}
          </Greeting>

          <Title
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            Hi, I'm <span>Igor</span>
          </Title>

          <TypingContainer
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            {typedText}
            <Cursor />
          </TypingContainer>

          <LocationLine
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <FaMapMarkerAlt size={11} /> {t.home.location}
          </LocationLine>

          <ButtonGroup
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65 }}
          >
            <PrimaryButton
              href="#portfolio"
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                document.getElementById('portfolio')?.scrollIntoView({ behavior: 'instant' as ScrollBehavior });
              }}
            >
              {t.home.viewWork} <FaArrowRight size={12} />
            </PrimaryButton>
            <SecondaryButton href="mailto:igorchrzyptowicz@gmail.com">
              {t.home.getInTouch}
            </SecondaryButton>
          </ButtonGroup>

          <SocialRow
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <a href="https://www.linkedin.com/in/igor-chrzyptowicz-3183911b7/" target="_blank" rel="noopener noreferrer">
              <FaLinkedin />
            </a>
            <a href="https://github.com/IgorLTS10" target="_blank" rel="noopener noreferrer">
              <FaGithub />
            </a>
            <a href="mailto:igorchrzyptowicz@gmail.com">
              <FaEnvelope />
            </a>
          </SocialRow>

          <StatsRow
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          >
            {t.home.stats.map((s) => (
              <Stat key={s.label}>
                <StatValue>{s.value}</StatValue>
                <StatLabel>{s.label}</StatLabel>
              </Stat>
            ))}
          </StatsRow>
        </TextSide>

        <PhotoSide
          initial={{ opacity: 0, scale: 0.88 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 0.7, ease: 'easeOut' }}
        >
          <PhotoRing>
            <PhotoInner>
              <Photo
                src={`${process.env.PUBLIC_URL}/IMG_2179.jpg`}
                alt="Igor Chrzyptowicz"
              />
            </PhotoInner>
          </PhotoRing>
        </PhotoSide>
      </Inner>
    </HomeContainer>
  );
};

export default Home;
